import dayjs from "dayjs";
import { useEffect } from "react";
import { LoaderFunction, redirect, useLoaderData, useNavigate } from "react-router-dom";

import { useConfetti } from "@/hooks";
import { useTranslation } from "@/core/i18n";
import { getAuthClaims, getPromotions } from "api/methods";
import { Models } from "api/types";
import { Button } from "atoms/button/Button";
import { Icon } from "atoms/icon/Icon";
import CameraFlip from "atoms/icon/icons/camera_flip.svg";
import Left from "atoms/icon/icons/chevron_left.svg";
import Right from "atoms/icon/icons/chevron_right.svg";
import Check from "atoms/icon/icons/ic_added_to_cart_check_ol.svg";
import OfferIcon from "atoms/icon/icons/ic_offers_ol.svg";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { BadgeTag } from "atoms/tag/BadgeTag";
import { ActionModal } from "molecules/modal-window/action-modal/ActionModal";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { FormattedString } from "utils/FormattedStrings";
import {
  getPromotionCardImageUrl,
  getPromotionCardTitle,
  getPromotionDetailDescription,
} from "utils/promotionPresentation";

type LoaderData = {
  promotion: Models<"PromotionModel">;
  claimed: boolean;
  comingFromScan: boolean;
  isAdminImpersonation: boolean;
};

export const walletOfferLoader: LoaderFunction = async ({ params: { offerId }, request }) => {
  if (!offerId) return redirect("/wallet/offers");
  const searchParams = new URL(request.url).searchParams;
  const claimed = searchParams.has("claimed");
  const comingFromScan = searchParams.has("comingFromScan");

  const promotions = await queryClient.fetchQuery({
    ...getPromotions.keyed(),
    staleTime: 1000 * 60 * 2,
  });

  const authClaims = await queryClient.fetchQuery({ ...getAuthClaims.keyed() });

  const isAdminImpersonation = !!authClaims?.claims.adminUserId;

  const promotion = promotions.find((p) => p.id === offerId);

  if (!promotion?.redeemedOn) return redirect("/wallet/offers");

  return {
    promotion,
    claimed,
    comingFromScan,
    isAdminImpersonation,
  };
};

const WalletOfferPage = () => {
  const { t } = useTranslation();
  const { promotion, claimed, comingFromScan, isAdminImpersonation } =
    useLoaderData() as LoaderData;
  const { triggerConfetti } = useConfetti();
  const navigate = useNavigate();

  const expiry = promotion.expiresOn ? dayjs(promotion.expiresOn) : null;
  const imageUrl = getPromotionCardImageUrl(promotion);
  const title = getPromotionCardTitle(promotion);
  const description = getPromotionDetailDescription(promotion);

  useEffect(() => {
    if (claimed) {
      setTimeout(() => {
        triggerConfetti();
      }, 1000);
    }
  }, [claimed]);

  const secondButtonProps = !comingFromScan
    ? {
        onSecondButtonClick: () => navigate(""),
        secondButtonLabel: t("walletOfferPage.viewOffer"),
      }
    : {};

  return (
    <div className="flex min-h-full flex-col">
      <TopNav startIconSVG={Left} onStartIconClick={() => navigate("/wallet/offers")}>
        <h1 className="flex items-center text-xl">{t("walletOfferPage.offers")}</h1>
      </TopNav>
      <div className="relative flex h-full grow flex-col">
        {imageUrl && (
          <>
            {expiry && (
              <div className="absolute left-3 top-3">
                <BadgeTag color="alert">
                  {t("walletOfferPage.expires", { time: expiry.fromNow() })}
                </BadgeTag>
              </div>
            )}
            <img
              className="aspect-video w-full object-cover"
              src={imageUrl}
              alt={t("walletOfferPage.promotionBannerAlt")}
            />
          </>
        )}
        <div className="flex grow flex-col items-center gap-6 px-4 py-6 text-primary">
          <h1 className="font-serif text-h2">{title}</h1>
          <FormattedString>{description}</FormattedString>
          {!promotion.isInStoreRedeemable && (
            <>
              <div className="flex cursor-pointer items-center justify-center rounded bg-two p-4 text-center">
                <div className="flex items-center gap-3">
                  <div className="flex items-center">
                    <Icon svg={OfferIcon} size="xsmall" color="secondaryText" />
                  </div>
                  <div className="text-left text-bold1 text-primary">
                    {t("walletOfferPage.alreadyRedeemed")}
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-4 text-center text-secondary">
                <div className="text-center text-body1 text-secondary">
                  {t("walletOfferPage.redeemedOn", {
                    date: dayjs(promotion.redeemedOn).format("ddd, L - hh:mma"),
                  })}
                </div>
                <p className="text-body2 italic text-secondary">{promotion.disclaimer}</p>
                <Button fullWidth style="white" onClick={() => navigate("/shop")}>
                  <div className="flex items-center">
                    <div>{t("walletOfferPage.exploreShop")}</div>
                    <Icon color="brandColor" svg={Right} size="x2small" />
                  </div>
                </Button>
              </div>
            </>
          )}
          {promotion.isInStoreRedeemable && (
            <>
              <div className="flex cursor-pointer items-center justify-center rounded bg-two p-4 text-center">
                <div className="flex items-center">
                  <div className="flex items-center">
                    <Icon svg={CameraFlip} size="xsmall" color="secondaryText" />
                  </div>
                  <div className="mr-6 pl-2 text-left text-body1 text-primary">
                    {t("walletOfferPage.showToStaff")}
                  </div>
                </div>
              </div>
              <div className="text-center text-body1 text-secondary">
                {t("walletOfferPage.redeemedOn", {
                  date: dayjs(promotion.redeemedOn).format("ddd, L - hh:mma"),
                })}
              </div>
              <div className="mb-5 rounded border p-2 text-center text-body1 text-secondary">
                {t("walletOfferPage.inOfficeCode", { code: promotion.promoCodeModel.code })}
              </div>
              <p className="text-center text-body2 italic text-secondary">{promotion.disclaimer}</p>
            </>
          )}
        </div>
        <RepeatMDFooter />
      </div>
      {claimed && (
        <ActionModal
          open={true}
          size="medium"
          onClose={() => navigate("")}
          headerIconSvg={Check}
          headerIconColor="brandColor"
          title={
            !comingFromScan && !isAdminImpersonation
              ? t("walletOfferPage.appliedToCartTitle")
              : t("walletOfferPage.claimedTitle")
          }
          description={
            <div className="flex h-full flex-col items-center gap-12 px-2.5 text-center">
              <div className="flex grow flex-col gap-8">
                <div className="text-body1 text-secondary">
                  {!comingFromScan
                    ? t("walletOfferPage.exploreShopDescription")
                    : t("walletOfferPage.tapBelowDescription")}
                </div>
              </div>
            </div>
          }
          firstButtonLabel={
            !comingFromScan ? t("walletOfferPage.shopNow") : t("walletOfferPage.viewConfirmation")
          }
          onFirstButtonClick={() => {
            comingFromScan ? navigate("") : navigate("/shop");
          }}
          {...secondButtonProps}
        />
      )}
    </div>
  );
};

export default WalletOfferPage;
